import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Suppress console.error in production to prevent info leakage
if (process.env.NODE_ENV === 'production') {
  const noop = function() {};
  console.error = noop;
  console.warn = noop;
  console.debug = noop;
}

import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardLayout from '@/components/DashboardLayout';
import PersonalDashboard from '@/pages/PersonalDashboard';
import TeamDashboard from '@/pages/TeamDashboard';
import ActivityLog from '@/pages/ActivityLog';
import ReportsPage from '@/pages/ReportsPage';
import RevenueMixReport from '@/pages/RevenueMixReport';
import TeamManagement from '@/pages/TeamManagement';
import LeadsListEnhanced from '@/pages/LeadsListEnhanced';
import LeadDetail from '@/pages/LeadDetail';
import AddLead from '@/pages/AddLead';
import CRMDashboard from '@/pages/CRMDashboard';
import SettingsPage from '@/pages/SettingsPage';
import ImportExportPage from '@/pages/ImportExportPage';
import CommunicationsPage from '@/pages/CommunicationsPage';
import PipelinePage from '@/pages/PipelinePage';
// Kanban Views
import PipelineKanban from '@/pages/PipelineKanban';
import LeadsKanban from '@/pages/LeadsKanban';
import ActivitiesHub from '@/pages/ActivitiesHub';
import CompanyDetail from '@/pages/CompanyDetail';
import ContactsPage from '@/pages/ContactsPage';
import ProductsPage from '@/pages/ProductsPage';
import AuthCallback from '@/pages/AuthCallback';
// Email Sequences
import SequencesPage from '@/pages/SequencesPage';
import SequenceBuilder from '@/pages/SequenceBuilder';
import SequenceDetail from '@/pages/SequenceDetail';
import EmailPage from '@/pages/EmailPage';
import ReplyInboxPage from '@/pages/ReplyInboxPage';
// Security
import SecurityDashboard from '@/pages/SecurityDashboard';
import AssignmentRulesPage from '@/pages/AssignmentRulesPage';
import AuditLogPage from '@/pages/AuditLogPage';
import ManagementHub from '@/pages/ManagementHub';
import ImpersonationBanner from '@/components/ImpersonationBanner';
// Unified Task Center (replaces Follow-ups, Reminders)
import TaskCenter from '@/pages/TaskCenter';
import TasksHub from '@/pages/TasksHub';
// Opportunities
import OpportunitiesPage from '@/pages/OpportunitiesPage';
import OpportunityDetail from '@/pages/OpportunityDetail';
import PreSalesDashboard from '@/pages/PreSalesDashboard';
import PreSalesCalendar from '@/pages/PreSalesCalendar';
import UnifiedCalendar from '@/pages/UnifiedCalendar';
import AccountsView from '@/pages/AccountsView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Setup axios defaults
// SOC2 hardening: httpOnly auth cookie is the primary credential.
// withCredentials=true ensures browsers send the cookie on same-origin requests.
// The Authorization header fallback is retained for a clean rollout window —
// new logins receive a cookie AND a token, so both paths work during migration.
axios.defaults.withCredentials = true;
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Role-based landing page — Pre-Sales roles land on the Pre-Sales dashboard,
// everyone else gets the SDR/personal dashboard.
export function getLandingPath(user) {
  if (!user) return '/';
  const role = user.role;
  if (role === 'pre_sales_engineer' || role === 'solutions_architect') {
    return '/presales';
  }
  return '/';
}

// ══════════════════════════════════════════════════════════════════════
//  Role-based Route Guard (iteration 77)
//  Pre-Sales users (pre_sales_engineer, solutions_architect) are
//  restricted to the narrow pre-sales workspace. Everyone else has
//  full CRM access, with a few routes (security, assignment-rules,
//  team-management) gated to admin+.
// ══════════════════════════════════════════════════════════════════════

const PRESALES_ALLOWED_PAGES = new Set([
  '/presales',
  '/presales/calendar',
  '/products',
  '/tasks',
  '/activity',
  '/log-activity',
  '/email',
  '/reports',
  '/reports/revenue-mix',
  '/team',
  '/settings',
]);

function isPresalesAllowed(pathname) {
  // Exact-match first, then prefix-match for detail pages under allowed roots.
  if (PRESALES_ALLOWED_PAGES.has(pathname)) return true;
  for (const p of PRESALES_ALLOWED_PAGES) {
    if (pathname.startsWith(p + '/')) return true;
  }
  return false;
}

// Legacy /companies URL → /accounts?mode=directory (preserves query params
// so the import deep-link `?batch=...` survives the redirect).
function CompaniesRedirect() {
  const loc = useLocation();
  const incoming = new URLSearchParams(loc.search);
  const out = new URLSearchParams();
  out.set('mode', 'directory');
  for (const [k, v] of incoming) {
    if (k !== 'mode') out.set(k, v);
  }
  return <Navigate to={`/accounts?${out.toString()}`} replace />;
}


function RouteGuard({ user, children, requiresAdmin = false, requiresManagerPlus = false, requiresTeamLeaderPlus = false }) {
  const location = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (!user) return <Navigate to="/login" replace />;
  // Pre-Sales workspace — hard lock
  const isPresalesRole = user.role === 'pre_sales_engineer' || user.role === 'solutions_architect';
  if (isPresalesRole && !isPresalesAllowed(location)) {
    return <Navigate to="/presales" replace />;
  }

  // Admin-only pages (e.g. Security, Allowed Domains management)
  if (requiresAdmin && user.role !== 'admin' && user.role !== 'company_sales_head') {
    return <Navigate to={getLandingPath(user)} replace />;  }

  // Manager-and-above pages (e.g. Team Management, Assignment Rules)
  if (requiresManagerPlus) {
    const managerPlus = new Set([
      'admin', 'company_sales_head', 'country_sales_head',
      'regional_sales_head', 'manager',
    ]);
    if (!managerPlus.has(user.role)) {
      return <Navigate to={getLandingPath(user)} replace />;
    }
  }

  // Team-Leader-and-above pages (e.g. Management Hub) — includes SA at level 3
  if (requiresTeamLeaderPlus) {
    const tlPlus = new Set([
      'admin', 'company_sales_head', 'country_sales_head',
      'regional_sales_head', 'manager', 'team_leader', 'solutions_architect',
    ]);
    if (!tlPlus.has(user.role)) {
      return <Navigate to={getLandingPath(user)} replace />;
    }
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Try /auth/me unconditionally — the httpOnly cookie (if present) will be
    // sent automatically. The localStorage token is a backward-compat fallback
    // attached by the axios interceptor.
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Only clear token on actual auth failures (401 Unauthorized)
      // Don't logout on rate limits (429), server errors (500), or network issues
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      } else {
        // For other errors (429, 500, network), retry once after a delay
        try {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const retryResponse = await axios.get(`${API}/auth/me`);
          setUser(retryResponse.data);
        } catch (retryError) {
          if (retryError.response?.status === 401) {
            localStorage.removeItem('token');
            setUser(null);
          }
          // For persistent non-auth errors, keep token but show nothing
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Backend sets httpOnly access_token cookie AND returns a token in body.
    // If the user hasn't completed first-time email verification, the backend
    // returns {requires_otp: true, otp_user_id, otp_email} — we surface that
    // upstream so the LoginPage can show its OTP step instead of finalising.
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data?.requires_otp) {
      return response.data; // do not set token / user yet
    }
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const verifyLoginOtp = async (otpUserId, otp) => {
    const response = await axios.post(`${API}/auth/otp/verify`, { user_id: otpUserId, otp });
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const resendLoginOtp = async (otpUserId) => {
    const response = await axios.post(`${API}/auth/otp/resend`, { user_id: otpUserId });
    return response.data;
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    // Clear server-side httpOnly cookie first so subsequent requests can't
    // replay it, then wipe any local fallback state.
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore network / server errors — we still want local logout to proceed
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const authValue = {
    user,
    login,
    verifyLoginOtp,
    resendLoginOtp,
    loginWithToken,
    register,
    logout,
    refreshUser: async () => {
      try {
        const r = await axios.get(`${API}/auth/me`);
        setUser(r.data);
        return r.data;
      } catch {
        return null;
      }
    },
    isAuthenticated: !!user,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider value={authValue}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  
  // CRITICAL: Check for session_id in hash synchronously during render (not in useEffect)
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
      <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
      <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/microsoft/callback" element={<AuthCallback />} />
      <Route path="/settings/microsoft-callback" element={<AuthCallback />} />
      <Route
        path="/*"
        element={
          user ? (
            <DashboardLayout>
              <Routes>
                <Route path="/" element={
                  (user?.role === 'pre_sales_engineer' || user?.role === 'solutions_architect')
                    ? <Navigate to="/presales" replace />
                    : <PersonalDashboard />
                } />
                <Route path="/crm-overview" element={<RouteGuard user={user}><CRMDashboard /></RouteGuard>} />
                <Route path="/leads" element={<RouteGuard user={user}><LeadsKanban /></RouteGuard>} />
                <Route path="/leads/:leadId" element={<RouteGuard user={user}><LeadDetail /></RouteGuard>} />
                <Route path="/add-lead" element={<RouteGuard user={user}><AddLead /></RouteGuard>} />
                <Route path="/pipeline" element={<RouteGuard user={user}><PipelinePage /></RouteGuard>} />
                <Route path="/companies" element={<RouteGuard user={user}><CompaniesRedirect /></RouteGuard>} />
                <Route path="/companies/:companyId" element={<RouteGuard user={user}><CompanyDetail /></RouteGuard>} />
                <Route path="/contacts" element={<RouteGuard user={user}><ContactsPage /></RouteGuard>} />
                <Route path="/products" element={<RouteGuard user={user}><ProductsPage /></RouteGuard>} />
                <Route path="/dashboard" element={
                  (user?.role === 'pre_sales_engineer' || user?.role === 'solutions_architect')
                    ? <Navigate to="/presales" replace />
                    : <PersonalDashboard />
                } />
                <Route path="/team" element={<RouteGuard user={user}><TeamDashboard /></RouteGuard>} />
                <Route path="/activity" element={<Navigate to="/activities" replace />} />
                <Route path="/log-activity" element={<RouteGuard user={user}><ActivityLog /></RouteGuard>} />
                <Route path="/reports" element={<RouteGuard user={user}><ReportsPage /></RouteGuard>} />
                <Route path="/reports/revenue-mix" element={<RouteGuard user={user}><RevenueMixReport /></RouteGuard>} />
                <Route path="/team-management" element={<RouteGuard user={user} requiresManagerPlus><TeamManagement /></RouteGuard>} />
                <Route path="/communications" element={<RouteGuard user={user}><CommunicationsPage /></RouteGuard>} />
                <Route path="/tasks" element={<RouteGuard user={user}><TasksHub /></RouteGuard>} />
                <Route path="/tasks-legacy" element={<RouteGuard user={user}><TaskCenter /></RouteGuard>} />
                {/* Redirect old routes to Task Center */}
                <Route path="/reminders" element={<Navigate to="/tasks" replace />} />
                <Route path="/follow-ups" element={<Navigate to="/tasks" replace />} />
                <Route path="/import-export" element={<RouteGuard user={user}><ImportExportPage /></RouteGuard>} />
                <Route path="/settings" element={<RouteGuard user={user}><SettingsPage /></RouteGuard>} />
                {/* Email Sequences */}
                <Route path="/sequences" element={<RouteGuard user={user}><SequencesPage /></RouteGuard>} />
                <Route path="/sequences/new" element={<RouteGuard user={user}><SequenceBuilder /></RouteGuard>} />
                <Route path="/sequences/:sequenceId" element={<RouteGuard user={user}><SequenceDetail /></RouteGuard>} />
                <Route path="/sequences/:sequenceId/edit" element={<RouteGuard user={user}><SequenceBuilder /></RouteGuard>} />
                <Route path="/email" element={<RouteGuard user={user}><EmailPage /></RouteGuard>} />
                <Route path="/reply-inbox" element={<RouteGuard user={user}><ReplyInboxPage /></RouteGuard>} />
                {/* Opportunities */}
                <Route path="/opportunities" element={<RouteGuard user={user}><OpportunitiesPage /></RouteGuard>} />
                <Route path="/opportunities/:id" element={<RouteGuard user={user}><OpportunityDetail /></RouteGuard>} />
                <Route path="/presales" element={<RouteGuard user={user}><PreSalesDashboard /></RouteGuard>} />
                <Route path="/presales/calendar" element={<RouteGuard user={user}><PreSalesCalendar /></RouteGuard>} />
                <Route path="/calendar" element={<RouteGuard user={user}><UnifiedCalendar /></RouteGuard>} />
                <Route path="/activities" element={<RouteGuard user={user}><ActivitiesHub /></RouteGuard>} />
                <Route path="/accounts" element={<RouteGuard user={user}><AccountsView /></RouteGuard>} />
                {/* Security - Admin Only */}
                <Route path="/security" element={<RouteGuard user={user} requiresAdmin><SecurityDashboard /></RouteGuard>} />
                <Route path="/admin/audit" element={<RouteGuard user={user} requiresAdmin><AuditLogPage /></RouteGuard>} />
                <Route path="/manage" element={<RouteGuard user={user} requiresTeamLeaderPlus><ManagementHub /></RouteGuard>} />
                {/* Assignment Rules - Admin/Manager */}
                <Route path="/assignment-rules" element={<RouteGuard user={user} requiresManagerPlus><AssignmentRulesPage /></RouteGuard>} />
              </Routes>
            </DashboardLayout>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
    </Routes>
  );
}

export default App;
export { API };